/**
 * The spoken reminder, played from a clip rather than synthesised on the phone.
 *
 * `speechSynthesis` cannot be driven from a timer on iOS — that is the whole reason reminders
 * were silent — but a decoded buffer can. The words are generated once on the server, kept in
 * R2, and fetched here well before the moment they are needed, so playback is a buffer start
 * and nothing else.
 */

import { audioContext, chimeSeconds, claimAudioSession, playChime, releaseAudioSession, reminderOutput, type ChimeTier } from "./chime";
import { report } from "./report";
import { shellFetch } from "./native";
import { speechVariantKey as variantKey, type SpeechVariant, type SpeechVoice } from "../shared/speech-catalogue";

/** Decoded and held, so the reminder never waits on a network round trip. */
const clips = new Map<string, AudioBuffer>();
const pending = new Set<string>();

/**
 * Fetch and decode a phrase ahead of time.
 *
 * Called when the plan changes rather than when the reminder fires: generation can take a
 * second or two the first time a phrase is ever asked for, which is not time a deadline has.
 *
 * The test line is the exception. Its words are written fresh by the model on every ask, so the
 * cache that keeps a reminder instant would otherwise pin the first joke forever.
 */
export async function prepareSpeech(deviceId: string, variant: SpeechVariant, voice: SpeechVoice): Promise<boolean> {
	const key = variantKey(variant, voice);
	if (clips.has(key) && variant.useCase !== "test") {
		return true;
	}
	if (pending.has(key)) {
		return false;
	}
	pending.add(key);
	try {
		const context = audioContext();
		if (!context) {
			return false;
		}
		const resolved = await shellFetch("/api/speech/resolve", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ deviceId, variant, voice }),
		});
		if (!resolved.ok) {
			return false;
		}
		const { audioUrl, text } = (await resolved.json()) as { audioUrl: string; text: string };
		const audio = await fetch(audioUrl);
		if (!audio.ok) {
			return false;
		}
		clips.set(key, await context.decodeAudioData(await audio.arrayBuffer()));
		report("speech-ready", { key, text });
		return true;
	} catch (cause) {
		report("speech-failed", { key, error: cause instanceof Error ? cause.message : String(cause) });
		return false;
	} finally {
		pending.delete(key);
	}
}

/**
 * How long a reminder may wait on the audio hardware before it stops being worth saying.
 *
 * Resuming a suspended context is not quick and is not bounded: on 2026-08-27 a refresh left it
 * suspended and `resume()` took 24.6 seconds to settle, by which time two reminders were queued
 * behind it and both played at once, five and ten minutes after their moments. Matches the window
 * the reminder scheduler itself uses — past a minute the moment has been overtaken.
 */
const SPEAK_STALE_MS = 60_000;

/** Only the newest reminder waiting on the audio hardware may have it. */
let speechTicket = 0;

/** Invalidate speech waiting on audio hardware when the plan no longer says it is due. */
export function cancelPendingSpeech(): void {
	speechTicket += 1;
}

/**
 * Sound the tone, then say it.
 *
 * The chime is what carries across a room and turns his head; the words are what he listens to
 * once it has. Spoken over the top they fight, so the clip is scheduled to begin as the tone ends.
 *
 * The fallback is the point: a reminder that fails silently is the fault this whole path exists
 * to fix, so a missing clip still makes a noise.
 */
export function speak(variant: SpeechVariant, voice: SpeechVoice, fallback: ChimeTier): void {
	const context = audioContext();
	const key = variantKey(variant, voice);
	const clip = clips.get(key);
	if (!context || !clip) {
		report("speech-fallback", { key, reason: context ? "no clip" : "no audio" });
		playChime(fallback);
		return;
	}
	const ticket = (speechTicket += 1);
	const requestedAtMs = Date.now();
	const start = context.state === "running" ? Promise.resolve() : context.resume().catch(() => undefined);
	void start.then(() => {
		const waitedMs = Date.now() - requestedAtMs;
		const superseded = ticket !== speechTicket;
		if (superseded || waitedMs > SPEAK_STALE_MS) {
			// Silence beats a stale reminder, and beats two of them talking over each other.
			report("speech-dropped", { key, waitedMs, superseded });
			return;
		}
		// Speech never claimed a session of its own: it leaned on whatever the last touch had set,
		// which is how a clip could report playing while the phone stayed silent.
		claimAudioSession();
		playChime(fallback);
		const lead = chimeSeconds(fallback) + 0.15;
		const source = context.createBufferSource();
		source.buffer = clip;
		source.connect(reminderOutput(context));
		source.start(context.currentTime + lead);
		releaseAudioSession(lead + clip.duration);
		report("speech", {
			key,
			state: context.state,
			waitedMs,
			chimeSeconds: Math.round(lead * 100) / 100,
			seconds: Math.round(clip.duration * 10) / 10,
		});
	});
}

/** The honest audition: the same fetch, decode and playback a real reminder takes. */
export async function testVoice(deviceId: string, voice: SpeechVoice): Promise<void> {
	const variant: SpeechVariant = { useCase: "test" };
	await prepareSpeech(deviceId, variant, voice);
	speak(variant, voice, "headsUp");
}
